import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  FormControl,
  TextInput,
  Button,
  ButtonGroup,
  Textarea,
  Text,
  Octicon,
} from "@primer/react";
import { CalendarIcon, PersonIcon, LocationIcon } from "@primer/octicons-react";
import { useForm, Controller } from "react-hook-form";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { IHocSinhNoiTruItemResponse } from "../../models/response/noi-tru/IHocSinhNoiTruItemResponse";
import { NotifyHelper } from "../../helpers/toast";
import { hocSinhNoiTruApi } from "../../api/hocSinhNoiTruApi";
import { ts_hocsinh_noitru } from "../../models/request/noi-tru/ts_hocsinh_noitru";
import ComboboxHocSinh from "../../components/combobox-hoc-sinh";

import ComboboxNoiTruTrangThai from "../../components/combobox-noitru-trangthai";
import ComboboxCrmKhoi from "../../components/combobox-crmkhoi";
import { ComboboxTruong } from "../../components/combobox-truong";
import ComboboxCrmHe from "../../components/combobox-crmhe";
import ComboboxCrmLop from "../../components/combobox-crmlop";
import ComboboxHocSinhNoiTru from "../../components/combobox-hoc-sinh-noitru";
import Modal from "../../components/ui/modal";
import { ComboboxNamHoc } from "../../components/combobox-namhoc";
import { Card } from "semantic-ui-react";
import UserAvatar from "../../components/user-avatar";
import ComboboxPhong from "../../components/combobox-phong";
import SelectBoxHocSinh from "../../components/selectbox-hocsinh";
import SelectBoxHocSinhNoiTru from "../../components/selectbox-hocsinh-noitru";
import ComboToaPhong from "../../components/combobox-toa-phong/ComboboxToaPhong";
import moment from "moment";

interface HocSinhNoiTruDetailModalProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData extends ts_hocsinh_noitru {
  dm_truong_id: number;
  dm_khoi_id: number;
  dm_he_id: number;
  dm_lop_id: number;
}

const HocSinhNoiTruDetailModal: React.FC<HocSinhNoiTruDetailModalProps> = ({
  id,
  onSuccess,
  onCancel,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [detailData, setDetailData] =
    useState<IHocSinhNoiTruItemResponse | null>(null);
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const defaultValues: FormData = {
    id: id,
    nam_hoc: nam_hoc,
    ts_hocsinh_noitru_status_id: 0,
    ts_hocsinh_id: 0,
    ngay_bat_dau_o: new Date().toISOString(),
    dm_phong_id: 0,
    ghi_chu: "",
    dm_truong_id: 0,
    dm_khoi_id: 0,
    dm_he_id: 0,
    dm_lop_id: 0,
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
    mode: "onSubmit", // Add this
  });

  const loadDetail = async () => {
    if (id > 0) {
        try {
            const res = await hocSinhNoiTruApi.detail(id);  
            if (res.is_success && res.data && Array.isArray(res.data) && res.data.length > 0) {
                const detailData = res.data[0];
                setDetailData(detailData);

                // Sử dụng UTC để xử lý ngày tháng
                let formattedDate = detailData.ngay_bat_dau_o;
                if (formattedDate) {
                    try {
                        // Chuyển đổi sang UTC để tránh vấn đề múi giờ
                        formattedDate = moment.utc(formattedDate).format('YYYY-MM-DD');
                    } catch (error) {
                        formattedDate = moment.utc().format('YYYY-MM-DD');
                    }
                }
  
                reset({
                    ...detailData,
                    ngay_bat_dau_o: formattedDate,
                });
            }
        } catch (error) {
            console.error("Load detail error:", error);
        }
    }
};
  useEffect(() => {
    loadDetail();
  }, [id]);

  const onSubmit = async (data: FormData) => {
    if (!data.ts_hocsinh_id) {
      NotifyHelper.Error(translate("Vui lòng chọn học sinh"));
      return;
    }
    if (Object.keys(errors).length > 0) {
      return;
    }
    setIsSaving(true);
    try {
      const submitData: ts_hocsinh_noitru = {
        id: data.id,
        nam_hoc: data.nam_hoc,
        ts_hocsinh_noitru_status_id: data.ts_hocsinh_noitru_status_id,
        ts_hocsinh_id: data.ts_hocsinh_id,
        ngay_bat_dau_o: data.ngay_bat_dau_o,
        dm_phong_id: data.dm_phong_id,
        ghi_chu: data.ghi_chu,
      };

      const api = id > 0 ? hocSinhNoiTruApi.update : hocSinhNoiTruApi.insert;
      const res = await api(submitData);

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id > 0 ? "Cập nhật thành công" : "Thêm mới thành công")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Có lỗi"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Có lỗi"));
    }
    setIsSaving(false);
  };

  return (
    <Modal
      isOpen
      title={translate(id > 0 ? "Cập nhật" : "Tạo mới")}
      onClose={onCancel}
      width="xlarge"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
        {detailData && (
          <Box
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 3,
              bg: "canvas.subtle",
              p: 3,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <UserAvatar fullName={detailData.ho_ten} size="large" />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Text sx={{ fontSize: 3, fontWeight: "bold", mb: 2 }}>
                {detailData.ho_ten}
              </Text>

              <Box sx={{ display: "flex", gap: 4, color: "fg.muted" }}>
                {detailData.ngay_sinh && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarIcon size={14} />
                    <Text>
                      {new Date(detailData.ngay_sinh).toLocaleDateString()}
                    </Text>
                  </Box>
                )}

                {detailData.dia_chi_tt && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationIcon size={14} />
                    <Text>{detailData.dia_chi_tt}</Text>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {!id ? ( // Chỉ hiển thị khi id = 0 (thêm mới)
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 3,
              mb: 3,
            }}
          >
            {/* <FormControl required>
              <FormControl.Label>{translate("Năm học")}</FormControl.Label>
              <Controller
                name="nam_hoc"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <ComboboxNamHoc
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setValue("dm_lop_id", 0);
                      setValue("ts_hocsinh_id", 0);
                    }}
                  />
                )}
              />
              {errors.nam_hoc && (
                <FormControl.Validation variant="error">
                  {errors.nam_hoc.message}
                </FormControl.Validation>
              )}
            </FormControl> */}
            <FormControl required sx={{ mb: 3 }}>
              <FormControl.Label>{translate("Học sinh")}</FormControl.Label>
              <Controller
                name="ts_hocsinh_id"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <SelectBoxHocSinhNoiTru
                    isReadonly={false}
                    value={field.value}
                    isShowClearBtn={true}
                    onValueChanged={(value, data) => {
                      field.onChange(value);
                      // if (data) {
                      //   setValue("dm_truong_id", data.dm_truong_id);
                      //   setValue("dm_khoi_id", data.dm_khoi_id);
                      //   setValue("dm_he_id", data.dm_he_id);
                      //   setValue("dm_lop_id", data.dm_lop_id);
                      // }
                    }}
                    width={"100%"}
                  />
                )}
              />
              {errors.ts_hocsinh_id && (
                <FormControl.Validation variant="error">
                  {errors.ts_hocsinh_id.message}
                </FormControl.Validation>
              )}
            </FormControl>
            {/* <FormControl required>
              <FormControl.Label>{translate("Trường")}</FormControl.Label>
              <Controller
                name="dm_truong_id"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <ComboboxTruong
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setValue("dm_khoi_id", 0);
                      setValue("dm_he_id", 0);
                      setValue("dm_lop_id", 0);
                      setValue("ts_hocsinh_id", 0);
                    }}
                  />
                )}
              />
              {errors.dm_truong_id && (
                <FormControl.Validation variant="error">
                  {errors.dm_truong_id.message}
                </FormControl.Validation>
              )}
            </FormControl>

            <FormControl required>
              <FormControl.Label>{translate("Khối")}</FormControl.Label>
              <Controller
                name="dm_khoi_id"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <ComboboxCrmKhoi
                    dm_truong_id={watch("dm_truong_id")}
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setValue("dm_lop_id", 0);
                      setValue("ts_hocsinh_id", 0);
                    }}
                  />
                )}
              />
              {errors.dm_khoi_id && (
                <FormControl.Validation variant="error">
                  {errors.dm_khoi_id.message}
                </FormControl.Validation>
              )}
            </FormControl>

            <FormControl required>
              <FormControl.Label>{translate("Hệ")}</FormControl.Label>
              <Controller
                name="dm_he_id"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <ComboboxCrmHe
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setValue("dm_lop_id", 0);
                      setValue("ts_hocsinh_id", 0);
                    }}
                  />
                )}
              />
              {errors.dm_he_id && (
                <FormControl.Validation variant="error">
                  {errors.dm_he_id.message}
                </FormControl.Validation>
              )}
            </FormControl>

            <FormControl required>
              <FormControl.Label>{translate("Lớp")}</FormControl.Label>
              <Controller
                name="dm_lop_id"
                control={control}
                rules={{ required: translate("Validation.Required") }}
                render={({ field }) => (
                  <ComboboxCrmLop
                    dm_truong_id={watch("dm_truong_id")}
                    nam_hoc={watch("nam_hoc")}
                    dm_khoi_id={watch("dm_khoi_id")}
                    dm_he_id={watch("dm_he_id")}
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setValue("ts_hocsinh_id", 0);
                    }}
                  />
                )}
              />
              {errors.dm_lop_id && (
                <FormControl.Validation variant="error">
                  {errors.dm_lop_id.message}
                </FormControl.Validation>
              )}
            </FormControl> */}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 3,
            mb: 3,
          }}
        >
          <FormControl required>
            <FormControl.Label>{translate("Trạng thái")}</FormControl.Label>
            <Controller
              name="ts_hocsinh_noitru_status_id"
              control={control}
              rules={{ required: translate("Validation.Required") }}
              render={({ field }) => (
                <ComboboxNoiTruTrangThai
                  value={field.value}
                  onValueChanged={(value) => field.onChange(value)}
                />
              )}
            />
            {errors.ts_hocsinh_noitru_status_id && (
              <FormControl.Validation variant="error">
                {errors.ts_hocsinh_noitru_status_id.message}
              </FormControl.Validation>
            )}
          </FormControl>
          <FormControl required>
            <Controller
              name="dm_phong_id"
              control={control}
              rules={{ required: translate("Validation.Required") }}
              render={({ field }) => (
                <ComboToaPhong
                  onValueChanged={(value) => field.onChange(value)}
                  dm_coso_id={dm_coso_id}
                  initialRoomId={field.value} // Chỉ cần giữ initialRoomId
                  isReadonly={false}
                />
              )}
            />
            {errors.dm_phong_id && (
              <FormControl.Validation variant="error">
                {errors.dm_phong_id.message}
              </FormControl.Validation>
            )}
          </FormControl>

          <FormControl required>
            <FormControl.Label>{translate("Ngày bắt đầu ở")}</FormControl.Label>
            <TextInput
              type="date"
              {...register("ngay_bat_dau_o", {
                required: translate("Validation.Required"),
                validate: (value) => {
                  const date = new Date(value);
                  return (
                    !isNaN(date.getTime()) ||
                    translate("Validation.InvalidDate")
                  );
                },
              })}
              value={(() => {
                const date = watch("ngay_bat_dau_o");
                if (!date) return new Date().toISOString().split("T")[0];
                try {
                  return new Date(date).toISOString().split("T")[0];
                } catch {
                  return new Date().toISOString().split("T")[0];
                }
              })()}
            />
            {errors.ngay_bat_dau_o && (
              <FormControl.Validation variant="error">
                {errors.ngay_bat_dau_o.message}
              </FormControl.Validation>
            )}
          </FormControl>
        </Box>

        <FormControl sx={{ mb: 3, width: "100%" }}>
          <FormControl.Label>{translate("Ghi chú")}</FormControl.Label>
          <Textarea rows={2} sx={{ width: "100%" }} {...register("ghi_chu")} />
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Modal>
  );
};

export default HocSinhNoiTruDetailModal;
